/**
 * The reader's shelf for one book, in the book page's side column.
 *
 * Signed out: one sentence on what an account gives, and the two links. It
 * never stands between a reader and the catalogue.
 *
 * Signed in: the reading status (want, reading, read, or none), whether they
 * own the book, a rating out of five, and a note only they can see. Real
 * form controls, a fieldset per group, saved with one button, so a keyboard
 * reaches everything and nothing is saved by accident. Under the note, a
 * second button publishes it as their review of the book (#107).
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Link, useLocation } from 'react-router'
import type { MessageKey } from '../../shared/message-keys.ts'
import { registerPath, signInPath } from '../../shared/return-path.ts'
import { NOTE_MAX_LENGTH, SHELF_STATUSES, shelfEntryInputSchema, type ShelfEntry, type ShelfEntryInput } from '../../shared/shelf.ts'
import {
  REVIEW_MAX_LENGTH,
  publishedBody,
  suspensionEnds,
  type PublicReview,
  type PublishNoteResponse,
  type ReviewsResponse,
  type WritingState,
} from '../../shared/reviews.ts'
import { useI18n } from '../i18n/index.tsx'
import { fill } from '../lib/catalogue.ts'
import { reviewsBase, reviewsChanged, send, useReviewsChanged } from '../lib/reviews-api.ts'
import { useShelf } from '../lib/shelf.tsx'
import { Alert } from './Alert.tsx'
import { CharacterCount } from './CharacterCount.tsx'

export function ShelfPanel({ world, work }: { world: string; work: string }) {
  const { t } = useI18n()
  const { state } = useShelf()
  const location = useLocation()

  if (state.status === 'signed-out') {
    return (
      <section className="side-card shelf" aria-labelledby="shelf-heading">
        <h2 id="shelf-heading" className="label">
          {t('shelf.title')}
        </h2>
        <p>{t('shelf.signed_out')}</p>
        <p className="shelf__links">
          <Link to={signInPath(location.pathname)}>{t('signin.title')}</Link>
          <Link to={registerPath(location.pathname)}>{t('register.title')}</Link>
        </p>
      </section>
    )
  }
  if (state.status === 'loading') return null
  if (state.status === 'error') {
    return (
      <section className="side-card shelf">
        <Alert tone="error">{t('shelf.load_failed')}</Alert>
      </section>
    )
  }
  return <ShelfForm key={work} world={world} work={work} entry={state.entries.get(work) ?? null} />
}

/**
 * Two kinds of change, saved two ways.
 *
 * The reading status and „Имам я“ save the moment they change, with
 * „Запазено“ and an undo: a reader who marks three books and moves on must
 * not lose them to a button they did not press, which would be worse than a
 * misclick, and a misclick is one tap to undo. The rating and the note are
 * deliberate text and keep their own save button; while they differ from
 * what is saved, the button says so. (A single Save button for everything
 * would need a guard against leaving the page with unsaved changes.)
 */
function ShelfForm({ world, work, entry }: { world: string; work: string; entry: ShelfEntry | null }) {
  const { t } = useI18n()
  const { save, noteMoved } = useShelf()
  // What the server has: the instant changes are applied to this.
  const saved: ShelfEntryInput = {
    status: entry?.status ?? null,
    owned: entry?.owned ?? false,
    rating: entry?.rating ?? null,
    note: entry?.note ?? null,
  }
  // What the controls show: the choice at once, reverted if the save fails.
  const [status, setStatus] = useState<ShelfEntryInput['status']>(saved.status)
  const [owned, setOwned] = useState(saved.owned)
  const [rating, setRating] = useState<number | null>(saved.rating)
  const [note, setNote] = useState(saved.note ?? '')
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<{ undo: ShelfEntryInput | null } | null>(null)
  const [error, setError] = useState<MessageKey | null>(null)

  const dirty = rating !== saved.rating || (note.trim() === '' ? null : note.trim()) !== saved.note
  // Set by a save that emptied a rating or note that had been there: then the
  // button says they were cleared, not saved.
  const [cleared, setCleared] = useState(false)

  /**
   * The button's label when nothing is waiting to be saved: what is saved, and
   * only that. It used to say „Оценката и бележката са запазени“ whatever was
   * there, even before anything had been entered. With nothing saved and
   * nothing cleared, it keeps the action's own label, disabled.
   */
  function savedLabel(): MessageKey {
    if (saved.rating !== null && saved.note !== null) return 'shelf.saved_text'
    if (saved.rating !== null) return 'shelf.saved_rating'
    if (saved.note !== null) return 'shelf.saved_note'
    return cleared ? 'shelf.cleared' : 'shelf.save_text'
  }

  async function commit(next: ShelfEntryInput, undo: ShelfEntryInput | null): Promise<boolean> {
    setError(null)
    setNotice(null)
    const parsed = shelfEntryInputSchema.safeParse(next)
    if (!parsed.success) {
      setError((parsed.error.issues[0]?.message ?? 'error.bad_request') as MessageKey)
      return false
    }
    setBusy(true)
    const failure = await save(work, parsed.data)
    setBusy(false)
    if (failure !== null) {
      setError(failure)
      return false
    }
    setNotice({ undo })
    return true
  }

  /** Status or owned: shown and saved at once, with the saved rating and note as they are. */
  function instant(change: Partial<Pick<ShelfEntryInput, 'status' | 'owned'>>, undo: ShelfEntryInput | null = saved) {
    const next = { ...saved, status, owned, ...change }
    setStatus(next.status)
    setOwned(next.owned)
    void commit(next, undo).then((ok) => {
      if (!ok) {
        setStatus(saved.status)
        setOwned(saved.owned)
      }
    })
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    const hadSomething = saved.rating !== null || saved.note !== null
    const ok = await commit({ ...saved, status, owned, rating, note }, null)
    if (ok) setCleared(hadSomething && rating === null && note.trim() === '')
  }

  return (
    <section className="side-card shelf" aria-labelledby="shelf-heading">
      <h2 id="shelf-heading" className="label">
        {t('shelf.title')}
      </h2>

      <fieldset className="shelf__group" disabled={busy}>
        <legend>{t('shelf.status.legend')}</legend>
        {[...SHELF_STATUSES, null].map((value) => (
          <label key={value ?? 'none'} className="shelf__choice">
            <input
              type="radio"
              name="status"
              checked={status === value}
              onChange={() => {
                instant({ status: value })
              }}
            />
            <span>{t(value === null ? 'shelf.status.none' : `shelf.status.${value}`)}</span>
          </label>
        ))}
      </fieldset>

      <label className="shelf__choice shelf__owned">
        <input
          type="checkbox"
          checked={owned}
          disabled={busy}
          onChange={(event) => {
            instant({ owned: event.target.checked })
          }}
        />
        <span>{t('shelf.owned')}</span>
      </label>

      <p className="shelf__saved" role="status">
        {notice === null ? null : (
          <>
            {t('shelf.saved')}{' '}
            {notice.undo === null ? null : (
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  if (notice.undo !== null) instant({ status: notice.undo.status, owned: notice.undo.owned }, null)
                }}
              >
                {t('shelf.undo')}
              </button>
            )}
          </>
        )}
      </p>

      <form onSubmit={(event) => void onSubmit(event)} noValidate>
        <fieldset className="shelf__group shelf__rating">
          <legend>{t('shelf.rating.legend')}</legend>
          {[1, 2, 3, 4, 5, null].map((value) => (
            <label key={value ?? 'none'} className="shelf__choice">
              <input
                type="radio"
                name="rating"
                checked={rating === value}
                onChange={() => {
                  setRating(value)
                  setNotice(null)
                }}
              />
              <span>{value === null ? t('shelf.rating.none') : String(value)}</span>
            </label>
          ))}
        </fieldset>

        <div className="field">
          <label className="field__label" htmlFor="shelf-note">
            {t('shelf.note.label')}
          </label>
          <p className="field__hint" id="shelf-note-hint">
            {t('shelf.note.hint')}
          </p>
          <textarea
            id="shelf-note"
            className="field__input field__input--area"
            rows={4}
            maxLength={NOTE_MAX_LENGTH}
            aria-describedby="shelf-note-hint shelf-note-count"
            value={note}
            onChange={(event) => {
              setNote(event.target.value)
              setNotice(null)
            }}
          />
          <CharacterCount id="shelf-note-count" count={note.length} limit={NOTE_MAX_LENGTH} />
        </div>

        {error ? <Alert tone="error">{t(error)}</Alert> : null}
        <button type="submit" className="button" disabled={busy || !dirty}>
          {t(busy ? 'shelf.saving' : dirty ? 'shelf.save_text' : savedLabel())}
        </button>
      </form>

      <PublishNote
        world={world}
        work={work}
        savedNote={saved.note}
        noteDirty={(note.trim() === '' ? null : note.trim()) !== saved.note}
        onPublished={(entryOutcome) => {
          if (entryOutcome !== 'kept') {
            setNote('')
            noteMoved(work, entryOutcome)
          }
          reviewsChanged(world, work)
        }}
      />
    </section>
  )
}

/**
 * Publish the saved note as the reader's review of this book (#107).
 *
 * The button says what it will do before the click, because the click cannot
 * be taken back: with no live review it creates one from the note; with one,
 * it appends the note after a blank line and says the resulting length. Either
 * way the note field is emptied. It publishes the note as saved, so it waits
 * while the note has unsaved changes.
 *
 * Disabled, it always says why: unsaved changes, a result over the limit, or
 * the strike rule (#98), whose own sentences are reused, because creating is
 * writing and appending is editing.
 */
function PublishNote({
  world,
  work,
  savedNote,
  noteDirty,
  onPublished,
}: {
  world: string
  work: string
  savedNote: string | null
  noteDirty: boolean
  onPublished: (entry: PublishNoteResponse['entry']) => void
}) {
  const { t, locale } = useI18n()
  const [own, setOwn] = useState<{ mine: PublicReview | null; writing: WritingState | null } | null>(null)
  const [spoilers, setSpoilers] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<MessageKey | null>(null)
  const [done, setDone] = useState<MessageKey | null>(null)

  const apply = useCallback((data: ReviewsResponse) => {
    const mine = data.reviews.find((review) => review.mine) ?? null
    setOwn({ mine, writing: data.writing })
    setSpoilers(mine?.spoilers ?? false)
  }, [])

  useEffect(() => {
    let live = true
    void send('GET', reviewsBase(world, work)).then((result) => {
      if (live && result.ok) apply(result.data as ReviewsResponse)
    })
    return () => {
      live = false
    }
  }, [world, work, apply])

  // The reader edited, deleted or wrote their review in the reviews section.
  const reload = useCallback(() => {
    void send('GET', reviewsBase(world, work)).then((result) => {
      if (result.ok) apply(result.data as ReviewsResponse)
    })
  }, [world, work, apply])
  useReviewsChanged(world, work, reload)

  const status = done === null ? null : <p role="status">{t(done)}</p>
  if (savedNote === null || own === null) return status === null ? null : <div className="shelf__publish">{status}</div>

  const plan = publishedBody(own.mine?.body ?? null, savedNote)
  const tooLong = plan.body.length > REVIEW_MAX_LENGTH
  const blocked = own.writing !== null && own.writing.state !== 'allowed'
  const counts = { total: plan.body.length, limit: REVIEW_MAX_LENGTH }

  async function onPublish() {
    setError(null)
    setDone(null)
    setBusy(true)
    const result = await send('POST', `/api/reviews/${encodeURIComponent(world)}/publish-note/${encodeURIComponent(work)}`, { spoilers })
    setBusy(false)
    if (!result.ok) {
      setError(result.key)
      return
    }
    const data = result.data as PublishNoteResponse
    setDone(data.entry === 'kept' ? 'shelf.publish.kept' : data.mode === 'create' ? 'shelf.publish.created' : 'shelf.publish.appended')
    onPublished(data.entry)
  }

  let reason: string | null = null
  if (blocked && own.writing !== null) {
    reason = own.writing.state === 'suspended' ? fill(t('reviews.suspended'), { date: suspensionEnds(locale, own.writing.until) }) : t('reviews.banned')
  } else if (noteDirty) reason = t('shelf.publish.save_first')
  else if (tooLong) reason = fill(t('error.publish_too_long'), counts)

  return (
    <div className="shelf__publish">
      <p className="field__hint" id="shelf-publish-hint">
        {plan.mode === 'create' ? t('shelf.publish.create_hint') : fill(t('shelf.publish.append_hint'), counts)}
      </p>
      <label className="shelf__choice">
        <input
          type="checkbox"
          checked={spoilers}
          // An existing warning is kept when the note is appended; the server keeps it too.
          disabled={busy || blocked || own.mine?.spoilers === true}
          onChange={(event) => {
            setSpoilers(event.target.checked)
          }}
        />
        <span>{t('reviews.spoilers.label')}</span>
      </label>
      {reason === null ? null : <p className="reviews__blocked">{reason}</p>}
      {error ? <Alert tone="error">{error === 'error.publish_too_long' ? fill(t(error), counts) : t(error)}</Alert> : null}
      <button
        type="button"
        className="button"
        aria-describedby="shelf-publish-hint"
        disabled={busy || reason !== null}
        onClick={() => void onPublish()}
      >
        {t(plan.mode === 'create' ? 'shelf.publish.create' : 'shelf.publish.append')}
      </button>
      {status}
    </div>
  )
}
